import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, ArrowUpCircle, CreditCard, Package, XCircle } from 'lucide-react';

const Shell = ({ children, testId }) => (
  <div className="min-h-screen flex flex-col bg-[#F7F9FC]" data-testid={testId}>
    <Header />
    <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-14">{children}</main>
    <Footer />
  </div>
);

export function BundlePurchasePage() {
  const { bundleId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!user) { navigate('/login', { state: { from: { pathname: `/bundles/${bundleId}` } } }); return; }
    api.get(`/bundles/${bundleId}/access-options`).then((r) => setOpts(r.data)).catch(() => setOpts({ error: true }));
  }, [api, bundleId, user, navigate]);

  const buy = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bundles/${bundleId}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start checkout'); setBusy(false); }
  };

  if (!opts) return <Shell testId="bundle-purchase-page"><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div></Shell>;
  if (opts.error) return <Shell testId="bundle-purchase-page"><p className="text-center text-slate-600">This bundle isn't available.</p></Shell>;
  const accessPath = opts.buyer === 'organisation' ? '/organisation/courses' : '/dashboard';
  return (
    <Shell testId="bundle-purchase-page">
      <span className="text-xs uppercase tracking-[0.16em] font-semibold text-[#20acf6]">Course bundle · {opts.course_count} courses</span>
      <h1 className="text-3xl sm:text-4xl font-bold text-[#2a3e5d] mt-2">{opts.title}</h1>
      {opts.owned || opts.included_in_subscription ? (
        <div className="mt-8 bg-white rounded-2xl border border-green-200 p-8 flex items-start gap-4" data-testid="bundle-already-access">
          <CheckCircle2 className="h-7 w-7 text-green-600 shrink-0" />
          <div>
            <p className="font-semibold text-slate-800 text-lg">{opts.owned ? 'You already own this bundle' : 'Included in your subscription'}</p>
            <p className="text-slate-600 text-sm mt-1">{opts.buyer === 'organisation' ? 'Assign these courses to your staff from Team Courses.' : 'Your courses are ready on your dashboard.'}</p>
            <Button asChild className="mt-4 bg-[#20acf6] hover:bg-[#3bb8f7]"><Link to={accessPath} data-testid="bundle-go-courses">Go to courses</Link></Button>
          </div>
        </div>
      ) : (
        <div className={`mt-8 grid gap-5 ${opts.buyer === 'organisation' ? 'md:grid-cols-2' : ''}`}>
          {opts.buyer === 'organisation' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-7 flex flex-col" data-testid="bundle-option-upgrade">
              <ArrowUpCircle className="h-7 w-7 text-[#02d1d4]" />
              <p className="font-bold text-[#2a3e5d] text-lg mt-3">Option A: Upgrade your subscription</p>
              <p className="text-sm text-slate-600 mt-1 flex-1">Move to a plan that includes this bundle, plus everything else in that tier.</p>
              <Button asChild variant="outline" className="mt-5 rounded-xl"><Link to={opts.upgrade_url} data-testid="bundle-upgrade-btn">See subscription plans</Link></Button>
            </div>
          )}
          <div className="bg-white rounded-2xl border-2 border-[#20acf6]/40 p-7 flex flex-col shadow-[0_12px_32px_-16px_rgba(32,172,246,0.45)]" data-testid="bundle-option-buy">
            <Package className="h-7 w-7 text-[#20acf6]" />
            <p className="font-bold text-[#2a3e5d] text-lg mt-3">{opts.buyer === 'organisation' ? 'Option B: ' : ''}Buy this bundle one-off</p>
            <p className="text-sm text-slate-600 mt-1 flex-1">A single payment, no subscription needed. {opts.buyer === 'organisation' ? 'Your organisation keeps permanent access to these courses, whatever plan you are on.' : 'You keep permanent access to every course in the bundle.'}</p>
            <p className="text-3xl font-bold text-[#2a3e5d] mt-5" data-testid="bundle-price">£{Number(opts.price || 0).toFixed(2)}</p>
            <Button onClick={buy} disabled={busy} className="mt-4 rounded-xl bg-[#2a3e5d] hover:bg-[#1e293b]" data-testid="bundle-buy-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />} Buy for £{Number(opts.price || 0).toFixed(2)}
            </Button>
          </div>
        </div>
      )}
    </Shell>
  );
}

const STATUS_URL = { bundle: (s) => `/bundles/checkout/status/${s}`, ebook: (s) => `/hub/checkout/status/${s}`, course: (s) => `/payments/status/${s}` };
const NEXT = { bundle: (st) => (st.buyer === 'organisation' ? '/organisation/courses' : '/dashboard'), ebook: () => '/knowledge-hub?tab=books', course: () => '/my-courses', subscription: () => '/organisation/subscription' };
const MESSAGE = {
  bundle: (st) => `${st.bundle_title || 'Your bundle'} is now yours to keep.`,
  ebook: (st) => `${st.product_title || 'Your e-book'} is ready to download.`,
  course: () => 'You are now enrolled — your course is waiting in My courses.',
  subscription: () => 'Your plan is active. Your team can start learning straight away.',
};
const normalise = (kind, d) => (kind === 'course' ? { ...d, status: d.payment_status === 'paid' ? 'paid' : d.status === 'expired' ? 'expired' : 'initiated' } : d);

export function PaymentReturnPage({ kind }) {
  const [params] = useSearchParams();
  const { api } = useAuth();
  const [state, setState] = useState({ status: 'checking' });
  const sid = params.get('session_id');
  useEffect(() => {
    if (kind === 'subscription') { setState({ status: 'paid' }); return undefined; }
    if (!sid) { setState({ status: 'missing' }); return undefined; }
    let tries = 0;
    let timer;
    const poll = () => api.get(STATUS_URL[kind](sid)).then((r) => {
      const d = normalise(kind, r.data);
      setState(d);
      if (d.status !== 'paid' && d.status !== 'expired' && ++tries < 10) timer = setTimeout(poll, 2000);
    }).catch(() => setState({ status: 'error' }));
    poll();
    return () => clearTimeout(timer);
  }, [api, sid, kind]);
  const paid = state.status === 'paid';
  const failed = ['expired', 'error', 'missing'].includes(state.status) || (!paid && state.status !== 'checking' && state.status !== 'initiated');
  const next = NEXT[kind](state);
  return (
    <Shell testId="payment-return-page">
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center max-w-lg mx-auto">
        {paid ? <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" /> : failed ? <XCircle className="h-12 w-12 text-red-500 mx-auto" /> : <Loader2 className="h-12 w-12 animate-spin text-[#20acf6] mx-auto" />}
        <h1 className="text-2xl font-bold text-[#2a3e5d] mt-4" data-testid="payment-return-status">
          {paid ? 'Payment successful' : failed ? 'Payment not completed' : 'Confirming your payment…'}
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          {paid ? MESSAGE[kind](state) : failed ? 'No money was taken. You can try again at any time.' : 'This only takes a few seconds.'}
        </p>
        {paid && kind === 'ebook' && state.access_token && (
          <>
            <p className="text-slate-500 mt-2 text-xs" data-testid="payment-return-email-note">We've also emailed a private download link to {state.email}.</p>
            <Button asChild className="mt-6 bg-green-600 hover:bg-green-700"><Link to={`/knowledge-hub/ebook/${state.access_token}`} data-testid="payment-return-download">Download your e-book</Link></Button>
          </>
        )}
        {(paid || failed) && !(paid && kind === 'ebook' && state.access_token) && <Button asChild className="mt-6 bg-[#20acf6] hover:bg-[#3bb8f7]"><Link to={next} data-testid="payment-return-continue">Continue</Link></Button>}
      </div>
    </Shell>
  );
}
